import {
  Badge,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@startupquickstart/frontend';
import { AlertTriangle, CheckCircle2, HelpCircle } from 'lucide-react';

interface SyncStatusBadgeProps {
  /**
   * Sync status from API
   * - true: In sync
   * - false: Out of sync
   * - null: Unable to determine (no hash data)
   */
  isInSync: boolean | null;

  /**
   * Label to show in tooltip when content is out of sync
   */
  outOfSyncMessage?: string;

  /**
   * Label to show in tooltip when content is in sync
   */
  inSyncMessage?: string;

  /**
   * Label to show in tooltip when status is unknown
   */
  unknownMessage?: string;

  /**
   * Show inline text label alongside icon
   */
  showLabel?: boolean;

  /**
   * Compact mode - smaller icons and no text
   */
  compact?: boolean;
}

/**
 * Display sync status with visual indicator
 * Shows whether voiceovers are in sync with content or translations are outdated
 */
export function SyncStatusBadge({
  isInSync,
  outOfSyncMessage = 'Content has changed since generation',
  inSyncMessage = 'Content is up to date',
  unknownMessage = 'Sync status unknown',
  showLabel = false,
  compact = false,
}: SyncStatusBadgeProps) {
  // Don't show anything if sync status is null and we're in compact mode
  if (isInSync === null && compact) {
    return null;
  }

  // Determine badge styling and content based on sync status
  const getBadgeProps = () => {
    if (isInSync === null) {
      return {
        variant: 'secondary' as const,
        icon: HelpCircle,
        label: 'Unknown',
        message: unknownMessage,
        iconClassName: 'text-muted-foreground',
      };
    }

    if (isInSync) {
      return {
        variant: 'outline' as const,
        icon: CheckCircle2,
        label: 'In Sync',
        message: inSyncMessage,
        iconClassName: 'text-green-600',
      };
    }

    return {
      variant: 'destructive' as const,
      icon: AlertTriangle,
      label: 'Out of Sync',
      message: outOfSyncMessage,
      iconClassName: 'text-destructive',
    };
  };

  const { variant, icon: Icon, label, message, iconClassName } = getBadgeProps();

  const iconSize = compact ? 'h-3 w-3' : 'h-4 w-4';

  const badgeContent = (
    <Badge variant={variant} className={compact ? 'px-1.5 py-0.5 text-xs' : ''}>
      <Icon className={`${iconSize} ${iconClassName}`} />
      {showLabel && !compact && <span className="ml-1.5">{label}</span>}
    </Badge>
  );

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center">{badgeContent}</span>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-sm">{message}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Simplified variant for showing only warning when out of sync
 */
export function SyncWarningBadge({
  isInSync,
  message = 'Content needs regeneration',
}: {
  isInSync: boolean | null;
  message?: string;
}) {
  // Only show if explicitly out of sync
  if (isInSync !== false) {
    return null;
  }

  return <SyncStatusBadge isInSync={isInSync} outOfSyncMessage={message} compact />;
}
